import React, { useEffect, useRef, useState } from "react";
import { X, ExternalLink } from "lucide-react";
import { useBooking } from "@/contexts/BookingContext";
import { useSiteContent } from "@/contexts/SiteContentContext";
import { HOME } from "@/lib/homeContent";
import { trackEvent } from "@/lib/analytics";
import { ENDORSEMENT } from "@/lib/brand";

const K = { fontFamily: "Kanit, Arial, sans-serif" };
const S = { fontFamily: "'Noto Serif', Georgia, serif" };

// Embedded Google Calendar appointment page. Google remains the source of truth for availability.
export default function BookingPanel() {
  const { isOpen, source, close } = useBooking();
  const { section } = useSiteContent();
  const c = section("booking", HOME.booking);
  const [loaded, setLoaded] = useState(false);
  const closeRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    setLoaded(false);
    trackEvent("booking_panel_open", { placement: source });
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKey);
    setTimeout(() => closeRef.current?.focus(), 50);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [isOpen, source, close]);

  if (!isOpen) return null;

  return (
    <div data-testid="booking-panel" role="dialog" aria-modal="true" aria-labelledby="booking-panel-title" className="fixed inset-0 z-[90] flex flex-col">
      <button type="button" aria-label="Close booking" onClick={close} className="absolute inset-0 bg-[#2B1017]/70 backdrop-blur-sm cursor-default" tabIndex={-1} />
      <div className="relative bg-[#F5EFE7] text-[#451C25] shadow-2xl animate-in slide-in-from-top duration-300 max-h-full flex flex-col">
        <div className="max-w-6xl mx-auto w-full px-5 md:px-8 pt-6 md:pt-8 pb-4 flex items-start justify-between gap-6">
          <div>
            <p className="text-[#897756] text-[11px] tracking-[0.04em] font-semibold" style={K}>
              <span className="text-[#451C25] uppercase tracking-[0.26em] text-[10px]">Destino Europe</span> · {ENDORSEMENT}
            </p>
            <h2 id="booking-panel-title" data-testid="booking-panel-title" className="mt-3 text-2xl md:text-4xl leading-[1.05] tracking-tight" style={{ ...K, fontWeight: 600 }}>{c.calendar_heading}</h2>
            <p className="mt-2 text-[#6B4954] text-[15px] md:text-base leading-relaxed max-w-xl" style={S}>{c.calendar_sub}</p>
          </div>
          <button ref={closeRef} type="button" data-testid="booking-panel-close" onClick={close} aria-label="Close" className="shrink-0 w-11 h-11 flex items-center justify-center border border-[#451C25]/25 hover:bg-[#451C25] hover:text-[#F5EFE7] transition-colors">
            <X size={18} />
          </button>
        </div>
        <div className="max-w-6xl mx-auto w-full px-5 md:px-8 pb-6 md:pb-8 flex-1 min-h-0">
          <div className="relative bg-white border border-[#451C25]/10 h-[62vh] min-h-[480px] md:h-[640px]">
            {!loaded && (
              <div data-testid="booking-panel-loading" className="absolute inset-0 flex items-center justify-center text-[#897756] text-[11px] tracking-[0.22em] uppercase" style={K}>Loading calendar…</div>
            )}
            <iframe
              data-testid="booking-calendar-iframe"
              title="Schedule a free call: Google Calendar"
              src={c.calendar_url}
              onLoad={() => setLoaded(true)}
              className={`w-full h-full border-0 transition-opacity duration-300 ${loaded ? "opacity-100" : "opacity-0"}`}
              loading="eager"
            />
          </div>
          <p className="mt-3 text-[#6B4954] text-[13px]" style={S}>
            Calendar not loading?{" "}
            <a data-testid="booking-open-new-tab" href={c.calendar_open_url} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent("schedule_call_click", { placement: source, mode: "new_tab_fallback" })} className="inline-flex items-center gap-1 underline underline-offset-4 hover:text-[#451C25]">
              {c.calendar_open_label} <ExternalLink size={12} />
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
